import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import AppHeader from '../components/layout/AppHeader';
import MyRewardPageHeader from '../components/bazzar/MyRewardPageHeader';
import WalletTabSwitcher from '../components/wallet/WalletTabSwitcher';
import WalletBalanceCard from '../components/wallet/WalletBalanceCard';
import WalletCurrencyInfoPanel from '../components/wallet/WalletCurrencyInfoPanel';
import VipRoomBreakdown from '../components/wallet/VipRoomBreakdown';
import TransactionCard from '../components/transactions/TransactionCard';
import XpKnowMoreDialog from '../components/bazzar/XpKnowMoreDialog';
import RapCoinKnowMoreDialog from '../components/bazzar/RapCoinKnowMoreDialog';
import RapGemKnowMoreDialog from '../components/bazzar/RapGemKnowMoreDialog';
import VipRoomKnowMoreDialog from '../components/bazzar/VipRoomKnowMoreDialog';
import {
  rapcoinCoinSrc,
  rapgemIconSrc,
  viproomIconSrc,
  walletBgSrc,
  xpCoinSrc,
} from '../assets/wallet';
import {
  WALLET_BAZAAR_TABS,
  WALLET_CURRENCIES,
  fetchWalletData,
  formatWalletBalance,
} from '../services/walletService';
import { WALLET_TAB_IAP_TYPE, fetchTransactions } from '../services/transactionService';
import { clearAuthSession } from '../storage/authStorage';
import { BAZZAR_PATH } from '../routes/paths';

const CURRENCY_ICONS = {
  xps: xpCoinSrc,
  rapcoins: rapcoinCoinSrc,
  rapgems: rapgemIconSrc,
  viproom: viproomIconSrc,
};

export default function MyWalletPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('xps');
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [knowMoreOpen, setKnowMoreOpen] = useState(false);
  const [tabTransactions, setTabTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const loadMoreRef = useRef(null);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);

  const loadWallet = useCallback(async () => {
    setLoading(true);

    try {
      const data = await fetchWalletData();
      setWallet(data);
    } catch {
      setWallet(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTabTransactions = useCallback(async (tab, page = 1, append = false) => {
    const type = WALLET_TAB_IAP_TYPE[tab];
    if (!type) return;

    if (page === 1) {
      setTransactionsLoading(true);
    } else {
      loadingMoreRef.current = true;
      setLoadingMore(true);
    }

    try {
      const { items, pagination } = await fetchTransactions({ page, type });

      setTabTransactions((prev) => (append ? [...prev, ...items] : items));
      pageRef.current = page;
      setHasMore(Boolean(pagination?.hasNextPage));
      hasMoreRef.current = Boolean(pagination?.hasNextPage);
    } catch {
      if (!append) setTabTransactions([]);
      setHasMore(false);
      hasMoreRef.current = false;
    } finally {
      if (page === 1) {
        setTransactionsLoading(false);
      } else {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, []);

  const loadMoreTransactions = useCallback(async () => {
    if (loadingMoreRef.current || !hasMoreRef.current) return;
    await loadTabTransactions(activeTab, pageRef.current + 1, true);
  }, [activeTab, loadTabTransactions]);

  useEffect(() => {
    loadWallet();
  }, [loadWallet]);

  useEffect(() => {
    setTabTransactions([]);
    pageRef.current = 1;
    setHasMore(false);
    hasMoreRef.current = false;
    loadTabTransactions(activeTab, 1, false);
  }, [activeTab, loadTabTransactions]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || transactionsLoading) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreTransactions();
        }
      },
      { root: null, rootMargin: '120px', threshold: 0 },
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [transactionsLoading, loadMoreTransactions, tabTransactions.length, activeTab]);

  const handleConfirmLogout = () => {
    clearAuthSession();
    window.location.href = '/';
  };

  const handleWalletAction = () => {
    const bazaarTabs = WALLET_BAZAAR_TABS[activeTab] ?? WALLET_BAZAAR_TABS.xps;
    navigate(BAZZAR_PATH, { state: bazaarTabs });
  };

  const currency = WALLET_CURRENCIES[activeTab];
  const currencyIcon = CURRENCY_ICONS[activeTab];
  const balance = wallet?.balances?.[activeTab] ?? 0;
  const listLoading = loading || transactionsLoading;

  return (
    <MainLayout>
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          backgroundImage: `url(${walletBgSrc})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ px: { md: '50px' } }}>
          <AppHeader endVariant="profile" onLogoutClick={() => setLogoutDialogOpen(true)} />
        </Box>

        <Box sx={{ px: { xs: 1.25, sm: 2, md: 3 }, pb: 6, pt: { xs: 1, md: 2 } }}>
          <MyRewardPageHeader title="My Wallet" backLabel="Back to Home" backTo={BAZZAR_PATH} />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 2.5, md: 3 },
              width: '100%',
              maxWidth: currency.description ? 1220 : 745,
              mx: 'auto',
            }}
          >
            <Box sx={{ width: '100%', maxWidth: 745, mx: currency.description ? 0 : 'auto' }}>
              <WalletTabSwitcher value={activeTab} onChange={setActiveTab} />
            </Box>

            {listLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress sx={{ color: '#ff00f2' }} />
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: currency.description ? 'row' : 'column' },
                  gap: { xs: 2.5, md: 3 },
                  alignItems: { md: 'flex-start' },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: 2.5, md: 3 },
                    width: '100%',
                    maxWidth: 745,
                    mx: currency.description ? 0 : 'auto',
                  }}
                >
                  <WalletBalanceCard
                    icon={currencyIcon}
                    label={currency.balanceLabel}
                    balanceLabel={
                      activeTab === 'viproom'
                        ? String(balance).padStart(2, '0')
                        : formatWalletBalance(balance)
                    }
                    redeemLabel={currency.actionLabel}
                    onRedeem={handleWalletAction}
                    warningMessage={activeTab === 'xps' ? wallet?.warningMessage : ''}
                    variant={currency.cardVariant}
                  >
                    {activeTab === 'viproom' ? (
                      <VipRoomBreakdown counts={wallet?.roomCounts} />
                    ) : null}
                  </WalletBalanceCard>

                  {tabTransactions.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                      <Typography
                        sx={{ color: '#e6d4ff', fontSize: 16, fontWeight: 600, textAlign: 'center' }}
                      >
                        No activity yet.
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {tabTransactions.map((transaction) => (
                        <TransactionCard
                          key={transaction.id}
                          transaction={transaction}
                          showDownloadInvoice={false}
                        />
                      ))}

                      {hasMore ? (
                        <Box
                          ref={loadMoreRef}
                          sx={{ display: 'flex', justifyContent: 'center', py: 2, minHeight: 48 }}
                        >
                          {loadingMore ? (
                            <CircularProgress size={28} sx={{ color: '#ff00f2' }} />
                          ) : null}
                        </Box>
                      ) : null}
                    </Box>
                  )}
                </Box>

                {currency.description ? (
                  <Box sx={{ width: { xs: '100%', md: 441 }, flexShrink: 0, mx: { xs: 'auto', md: 0 } }}>
                    <WalletCurrencyInfoPanel
                      icon={currencyIcon}
                      description={currency.description}
                      onKnowMore={() => setKnowMoreOpen(true)}
                    />
                  </Box>
                ) : null}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <XpKnowMoreDialog
        open={knowMoreOpen && activeTab === 'xps'}
        onClose={() => setKnowMoreOpen(false)}
      />
      <RapCoinKnowMoreDialog
        open={knowMoreOpen && activeTab === 'rapcoins'}
        onClose={() => setKnowMoreOpen(false)}
      />
      <RapGemKnowMoreDialog
        open={knowMoreOpen && activeTab === 'rapgems'}
        onClose={() => setKnowMoreOpen(false)}
      />
      <VipRoomKnowMoreDialog
        open={knowMoreOpen && activeTab === 'viproom'}
        onClose={() => setKnowMoreOpen(false)}
      />

      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: '#1a0834',
            border: '1px solid #ff66f766',
            color: '#fff',
            minWidth: { xs: '90%', sm: 380 },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#fff' }}>Logout</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#e6d4ff' }}>Are you sure you want to logout?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLogoutDialogOpen(false)} sx={{ color: '#e6d4ff', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmLogout}
            variant="contained"
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(90deg, #822AFF 0%, #FF00F2 100%)',
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
